// Router minimalista basado en hash. Sin dependencias.
//
// Las rutas se declaran con patrones tipo '#/pedido/:id' — el handler recibe
// `(params, query)` y devuelve un objeto con `mount(root)` y opcionalmente
// `unmount()`. El router invoca `unmount` antes de cambiar de ruta.

const routes = [];
let _currentUnmount = null;
let _root = null;
let _authGuard = null;
let _adminGuard = null;

export function defineRoute(pattern, handler, { auth = true, admin = false } = {}) {
  routes.push({ pattern, handler, auth, admin });
}

/** Registra el guard que decide si la ruta `auth=true` puede entrar.
 *  Devuelve true para permitir; false para redirigir a /login. */
export function setAuthGuard(fn) { _authGuard = fn; }

/** Guard adicional para rutas `admin=true`. Si el user no es admin,
 *  redirige a /pedidos (no a /login — sí está logueado, solo no autorizado). */
export function setAdminGuard(fn) { _adminGuard = fn; }

/** Comparador de patrones tipo '#/pedido/:id' contra '#/pedido/42'. */
function _match(pattern, hash) {
  const a = pattern.replace(/^#?\/?/, '').split('/').filter(Boolean);
  const [pathRaw, queryRaw] = hash.replace(/^#?\/?/, '').split('?');
  const b = pathRaw.split('/').filter(Boolean);
  if (a.length !== b.length) return null;
  const params = {};
  for (let i = 0; i < a.length; i++) {
    if (a[i].startsWith(':')) params[a[i].slice(1)] = decodeURIComponent(b[i]);
    else if (a[i] !== b[i]) return null;
  }
  const query = Object.fromEntries(new URLSearchParams(queryRaw || '').entries());
  return { params, query };
}

async function _resolve() {
  if (!_root) return;
  const hash = location.hash || '#/';
  for (const r of routes) {
    const m = _match(r.pattern, hash);
    if (!m) continue;
    if (r.auth && _authGuard) {
      const ok = await _authGuard();
      if (!ok) { navigate('#/login', { replace: true }); return; }
    }
    if (r.admin && _adminGuard) {
      const ok = await _adminGuard();
      if (!ok) { navigate('#/pedidos', { replace: true }); return; }
    }
    if (typeof _currentUnmount === 'function') {
      try { _currentUnmount(); } catch (e) { console.warn('unmount falló:', e); }
      _currentUnmount = null;
    }
    _root.innerHTML = '';
    try {
      const view = await r.handler(m.params, m.query);
      const result = view?.mount?.(_root);
      _currentUnmount = view?.unmount || null;
      if (result instanceof Promise) await result;
    } catch (e) {
      console.error('Error montando vista', hash, e);
      _root.innerHTML = `<div style="padding:1rem;color:#b91c1c">Error: ${e.message}</div>`;
    }
    return;
  }
  // Sin match → fallback al home
  navigate('#/', { replace: true });
}

export function navigate(hash, { replace = false } = {}) {
  if (location.hash === hash) { _resolve(); return; }
  if (replace) location.replace(hash);
  else location.hash = hash;
}

export function start(rootEl) {
  _root = rootEl;
  window.addEventListener('hashchange', _resolve);
  if (!location.hash) location.hash = '#/';
  else _resolve();
}
